import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { formatDateTime } from '@/lib/utils';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface HistoryEntry {
  id?: string;
  action?: string;
  fromStatus?: string;
  toStatus?: string;
  remarks?: string;
  performedAt?: string;
  performedByName?: string;
  performedByRole?: string;
  [key: string]: unknown;
}

interface ApprovalHistoryProps {
  history?: HistoryEntry[] | null;
}

const NOTE_COLLAPSE_LENGTH = 200;

function getEntry(entry: HistoryEntry, camel: string, snake: string): string | undefined {
  const v = entry[camel];
  if (v !== undefined && v !== null) return String(v);
  const s = entry[snake];
  if (s !== undefined && s !== null) return String(s);
  return undefined;
}

function roleLabel(role?: string): string {
  const norm = (role || '').toUpperCase().replace(/\s+/g, '_');
  if (norm === 'PROJECT_MANAGER') return 'Project Manager';
  if (norm === 'MANAGER') return 'Manager';
  if (norm === 'CLUSTER_HEAD' || norm === 'CLUSTER HEAD') return 'Cluster Head';
  if (norm === 'ENGINEER') return 'Site Engineer';
  if (norm === 'ADMIN' || norm === 'ORG_ADMIN') return 'System Admin';
  if (norm === 'VIEWER') return 'Viewer';
  return role || '';
}

function actionMeta(action?: string) {
  const a = (action || '').toLowerCase();
  if (a.includes('approve')) return { label: 'Approved', tone: 'text-green-700 dark:text-green-400' };
  if (a.includes('reject') || a.includes('disapprove')) return { label: 'Rejected', tone: 'text-red-600 dark:text-red-400' };
  if (a.includes('submit') || a.includes('return')) return { label: a.includes('return') ? 'Returned' : 'Submitted', tone: 'text-blue-600 dark:text-blue-400' };
  return { label: a || 'Action', tone: 'text-secondary' };
}

function NoteBlock({ note }: { note: string }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = note.length > NOTE_COLLAPSE_LENGTH;
  const showFull = expanded || !isLong;

  return (
    <div className="mt-2">
      <p className="text-xs font-medium uppercase tracking-wide text-secondary">Note</p>
      <p
        className="mt-1 text-sm text-foreground whitespace-pre-wrap break-words break-all"
        style={!showFull ? { maxHeight: '6rem', overflow: 'hidden' } : undefined}
      >
        {showFull ? note : `${note.slice(0, NOTE_COLLAPSE_LENGTH)}…`}
      </p>
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300"
        >
          {expanded ? (
            <>
              Show less <ChevronUp size={12} />
            </>
          ) : (
            <>
              Show more <ChevronDown size={12} />
            </>
          )}
        </button>
      )}
    </div>
  );
}

export default function ApprovalHistory({ history }: ApprovalHistoryProps) {
  const entries = history || [];

  if (entries.length === 0) return null;

  const sorted = [...entries].sort((a, b) => {
    const ta = getEntry(a, 'performedAt', 'performed_at');
    const tb = getEntry(b, 'performedAt', 'performed_at');
    return new Date(tb || 0).getTime() - new Date(ta || 0).getTime();
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Approval History</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {sorted.map((h, idx) => {
            const action = getEntry(h, 'action', 'action');
            const meta = actionMeta(action);
            const role = roleLabel(getEntry(h, 'performedByRole', 'performed_by_role'));
            const name = getEntry(h, 'performedByName', 'performed_by_name');
            const performedAt = getEntry(h, 'performedAt', 'performed_at');
            const note = getEntry(h, 'remarks', 'remarks');
            const fromStatus = getEntry(h, 'fromStatus', 'from_status');
            const toStatus = getEntry(h, 'toStatus', 'to_status');

            const border =
              action?.toLowerCase().includes('reject') || action?.toLowerCase().includes('disapprove')
                ? 'border-red-500'
                : action?.toLowerCase().includes('approve')
                  ? 'border-green-500'
                  : 'border-blue-500';

            return (
              <div key={h.id || `entry-${idx}`} className={`border-l-2 pl-3 py-1 ${border}`}>
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  {role && <span className="text-sm font-semibold text-primary">{role}</span>}
                  <span className={`text-sm font-medium ${meta.tone}`}>{meta.label}</span>
                  {name && <span className="text-xs text-secondary">by {name}</span>}
                </div>
                <p className="text-xs text-muted mt-0.5">{formatDateTime(performedAt)}</p>
                {fromStatus && toStatus && (
                  <p className="text-xs text-secondary mt-1">
                    {fromStatus} &rarr; {toStatus}
                  </p>
                )}
                {note && <NoteBlock note={note} />}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
